/**
 * Offline Indicator Component
 * Displays network connectivity status within the chat widget
 * Shows offline state with helpful messaging and reconnection info
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useOnlineStatus } from '../../../../shared/hooks/useOnlineStatus';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface OfflineIndicatorProps {
  className?: string;
}

/**
 * Offline indicator for chat widget
 * Shows when user loses network connectivity
 */
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  className,
}) => {
  const { isOnline, wasOffline } = useOnlineStatus();
  const { prefersReducedMotion, tween } = useReducedMotion();

  // Don't render if online and wasn't recently offline
  if (isOnline && !wasOffline) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      {!isOnline && (
        <motion.div
          key="offline"
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
          transition={tween}
          className={cn(
            'flex items-center gap-2 px-3 py-2',
            'bg-amber-50 dark:bg-amber-900/20',
            'border-b border-amber-200 dark:border-amber-800',
            'text-amber-800 dark:text-amber-200',
            className
          )}
          role="alert"
          aria-live="polite"
        >
          <WifiOff className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium flex-1">
            You're offline
          </span>
          <span className="text-xs text-amber-600 dark:text-amber-400">
            Messages will send when you reconnect
          </span>
          <RefreshCw
            className="w-4 h-4 shrink-0 opacity-60"
            aria-hidden="true"
          />
        </motion.div>
      )}

      {isOnline && wasOffline && (
        <motion.div
          key="back-online"
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
          transition={tween}
          className={cn(
            'flex items-center gap-2 px-3 py-2',
            'bg-emerald-50 dark:bg-emerald-900/20',
            'border-b border-emerald-200 dark:border-emerald-800',
            'text-emerald-800 dark:text-emerald-200',
            className
          )}
          role="alert"
          aria-live="polite"
        >
          <CheckCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium">
            You're back online
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Compact offline badge for chat header
 */
export const OfflineBadge: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { isOnline } = useOnlineStatus();
  const { prefersReducedMotion, tween } = useReducedMotion();

  if (isOnline) return null;

  return (
    <motion.span
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
      transition={tween}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5',
        'bg-amber-100 dark:bg-amber-900/30',
        'text-amber-700 dark:text-amber-300',
        'text-xs font-medium rounded-full',
        className
      )}
    >
      <WifiOff className="w-3 h-3" aria-hidden="true" />
      <span>Offline</span>
    </motion.span>
  );
};

export default OfflineIndicator;
